import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { CirclePlus, Info, Plus } from 'lucide-react';
import TextareaAutosize from 'react-textarea-autosize';
import {
  applySheetRowFieldPatches,
  buildCommerceListingOfferByCatalogMaterialId,
  buildRowsForCommerceSheetConfirm,
  collectSheetPreviewCatalogMaterialIds,
  fetchCommerceCatalogMaterialsForPreviewListing,
  formatSheetRowPriceDisplay,
  formatSheetRowPriceInputValue,
  formatSheetRowPromotionDiscountInputValue,
  getSheetRowCategoryDisplay,
  getSheetRowDefaultRawName,
  getSheetRowDescriptionSeedFromSource,
  getSheetRowPromotionDiscountSeedFromRow,
  getSheetRowPromotionSeedFromSource,
  readSheetRowOfferDefaults,
  parseSheetPriceInputToNumber,
  parseSheetPromotionDiscountInputToNumber,
  readSheetRowCatalogMaterialId,
  readSheetRowPriceValue,
  readSheetRowPromotionDiscountValue,
  readSheetRowUnitDisplay,
  readSheetRowResolvedUserCategoryId,
  sheetConfirmSummaryLinesForDisplay,
  sheetMergedRowMatchesCommerceListing,
  initRematchMapForPreviewRows,
  previewRowsMissingCompletenessBlock,
  readSheetRowReasonCodesHint,
  readSheetRowSuggestedCatalogMaterialName,
  resolveSheetRematchDisplayedRawName,
  rowMeetsSheetConfirmMinimums,
  rowUnitRequiresKnownSelectValue,
  rowUsesCatalogDirectConfirm,
  type SheetCategoryOption,
  type SheetConfirmSubmitResult,
  type SheetConfirmSummaryUi,
  type SheetManualAddRowPayload,
  type SheetRematchByRowIndex,
  type SheetRematchPick,
  type SheetRowFieldPatch,
} from '../../services/materialCommerceSheetService.ts';
import { type ParsedCategory } from '../Perfil/PerfilMain/MisMaterialesComercio/commerceMaterialsModel.ts';
import addMaterialPopupStyles from '../MarketPlace/CalculadoraDeMateriales/AddCustomMaterialPopup.module.scss';
import {
  coerceRawUnitToMaterialSelectValue,
  MATERIAL_UNIT_SELECT_OPTIONS,
} from '../../utils/marketplace/materialUnitSelectOptions.ts';
import CustomSelect from '../../pages/ProjectDocumentation/components/CustomSelect.tsx';
import ModalOverlay from '../UI/ModalOverlay/ModalOverlay.tsx';
import formStyles from '../OnboardingV2/ConversationalOnboarding/CommerceOnboarding/CommerceForm.module.scss';
import styles from './CommerceSheetReviewStep.module.scss';

function priceInputValue(eff: unknown, patch: SheetRowFieldPatch | undefined): string {
  if (patch?.price !== undefined) {
    const s = String(patch.price);
    if (!s.trim()) return '';
    return s;
  }
  const n = readSheetRowPriceValue(eff);
  return n != null ? formatSheetRowPriceInputValue(n) : '';
}

function unitInputValue(eff: unknown, patch: SheetRowFieldPatch | undefined): string {
  if (patch?.unit !== undefined) return patch.unit.trim();
  return readSheetRowUnitDisplay(eff);
}

function unitSelectTableValue(eff: unknown, patch: SheetRowFieldPatch | undefined): string {
  return coerceRawUnitToMaterialSelectValue(unitInputValue(eff, patch));
}

function descInputValue(row: unknown, patch: SheetRowFieldPatch | undefined): string {
  if (patch?.rawName !== undefined) return patch.rawName;
  return getSheetRowDefaultRawName(row);
}

function descriptionOfferInputValue(row: unknown, patch: SheetRowFieldPatch | undefined): string {
  if (patch?.description !== undefined) return patch.description;
  if (row != null && typeof row === 'object' && !Array.isArray(row)) {
    const d = (row as Record<string, unknown>).description;
    if (typeof d === 'string') return d;
  }
  return getSheetRowDescriptionSeedFromSource(row);
}

function promotionOfferInputValue(row: unknown, patch: SheetRowFieldPatch | undefined): string {
  if (patch?.promotion !== undefined) return patch.promotion;
  if (row != null && typeof row === 'object' && !Array.isArray(row)) {
    const p = (row as Record<string, unknown>).promotion;
    if (typeof p === 'string') return p;
  }
  return getSheetRowPromotionSeedFromSource(row);
}

function promotionDiscountInputValue(eff: unknown, patch: SheetRowFieldPatch | undefined): string {
  if (patch?.promotionDiscount !== undefined) {
    const s = String(patch.promotionDiscount);
    if (!s.trim()) return '';
    return s;
  }
  const n = readSheetRowPromotionDiscountValue(eff);
  return n != null ? formatSheetRowPromotionDiscountInputValue(n) : '';
}

function sheetReviewRowFingerprint(
  row: unknown,
  patch: SheetRowFieldPatch | undefined,
  rematchPick: SheetRematchPick
): string {
  const eff = applySheetRowFieldPatches(row, patch);
  const defs = readSheetRowOfferDefaults(eff);
  const price = readSheetRowPriceValue(eff);
  const cat = rematchPick.userCategoryId === '' ? '' : String(rematchPick.userCategoryId);
  const raw = (rematchPick.rawName ?? '').trim();
  const name = getSheetRowDefaultRawName(eff).trim();
  const unit = readSheetRowUnitDisplay(eff);
  const desc = descriptionOfferInputValue(eff, patch).trim();
  const promo = promotionOfferInputValue(eff, patch).trim();
  const pd = readSheetRowPromotionDiscountValue(eff);
  return JSON.stringify({
    price,
    unquoted: defs.unquoted,
    vat: defs.valueAddedTax,
    cat,
    raw,
    name,
    unit,
    desc,
    promo,
    pd: pd != null ? pd : null,
    createCatalog: rematchPick.createCatalogMaterial === true,
    rejectSuggested: rematchPick.rejectSuggestedCatalogMatch === true,
  });
}

function isRowIncluded(excludedByRowIndex: Record<number, boolean> | undefined, i: number): boolean {
  return !excludedByRowIndex?.[i];
}

interface CommerceSheetReviewStepProps {
  previewRows: unknown[];
  categories: SheetCategoryOption[];
  rematchByRowIndex: SheetRematchByRowIndex;
  rowFieldPatches?: Record<number, SheetRowFieldPatch>;
  onRowFieldPatch?: (rowIndex: number, patch: Partial<SheetRowFieldPatch>) => void;
  excludedByRowIndex?: Record<number, boolean>;
  onExcludedChange?: (rowIndex: number, excluded: boolean) => void;
  onAddManualRow?: (payload: SheetManualAddRowPayload) => Promise<{ ok: boolean; message?: string }>;
  onRemovePreviewRow?: (rowIndex: number) => void;
  onRematchChange: (rowIndex: number, patch: Partial<SheetRematchPick>) => void;
  onConfirm: () => Promise<SheetConfirmSubmitResult>;
  onSuccessContinue: () => void;
  onBack: () => void;
  errorText?: string | null;
  successContinueLabel?: string;
  commerceId?: number | null;
  commerceMaterialsCategories?: ParsedCategory[];
}

const defaultSuccessContinueLabel = 'Ir a Gestión → Mi comercio';

const profileMaterialsHint =
  'Podés revisar el listado completo en tu perfil, pestaña Gestión → Mi comercio.';

const COL_COUNT = 5;
const SHEET_REVIEW_UI_PAGE_SIZE = 50;
const SHEET_REVIEW_TABLE_SCROLL_OFFSET_PX = 80;

const addMaterialPopupVariants = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: { opacity: 1, scale: 1 },
};

export const CommerceSheetReviewStep: React.FC<CommerceSheetReviewStepProps> = ({
  previewRows,
  categories,
  rematchByRowIndex,
  rowFieldPatches = {},
  onRowFieldPatch,
  excludedByRowIndex = {},
  onExcludedChange,
  onAddManualRow,
  onRemovePreviewRow,
  onRematchChange,
  onConfirm,
  onSuccessContinue,
  onBack,
  errorText,
  successContinueLabel = defaultSuccessContinueLabel,
  commerceId = null,
  commerceMaterialsCategories: commerceMaterialsCategoriesProp,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');
  const [savedSummary, setSavedSummary] = useState<SheetConfirmSummaryUi | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addModalSubmitting, setAddModalSubmitting] = useState(false);
  const [addModalError, setAddModalError] = useState('');
  const [modCategoryId, setModCategoryId] = useState('');
  const [modRawName, setModRawName] = useState('');
  const [modDesc, setModDesc] = useState('');
  const [modPromo, setModPromo] = useState('');
  const [modDisc, setModDisc] = useState('');
  const [modPrice, setModPrice] = useState('');
  const [modUnit, setModUnit] = useState('');
  const [modUnquoted, setModUnquoted] = useState(false);
  const [modVat, setModVat] = useState(true);
  const [sheetFilterQuery, setSheetFilterQuery] = useState('');
  const [sheetStatusFilter, setSheetStatusFilter] = useState<'all' | 'incomplete' | 'complete' | 'unchanged'>('all');
  const [sheetReviewUIPage, setSheetReviewUIPage] = useState(1);
  const [fetchedMaterialsCategories, setFetchedMaterialsCategories] = useState<ParsedCategory[] | null>(null);
  const unchangedBootstrapRef = useRef<Set<number>>(new Set());

  const patchField = useCallback(
    (rowIndex: number, patch: Partial<SheetRowFieldPatch>) => {
      onRowFieldPatch?.(rowIndex, patch);
    },
    [onRowFieldPatch]
  );

  const categorySelectOptions = useMemo(
    () => categories.map((c) => ({ value: String(c.id), label: c.name })),
    [categories]
  );

  const resetAddModalForm = useCallback(() => {
    setModCategoryId('');
    setModRawName('');
    setModDesc('');
    setModPromo('');
    setModDisc('');
    setModPrice('');
    setModUnit('');
    setModUnquoted(false);
    setModVat(true);
    setAddModalSubmitting(false);
    setAddModalError('');
  }, []);

  const openAddMaterialModal = useCallback(() => {
    resetAddModalForm();
    setAddModalOpen(true);
  }, [resetAddModalForm]);

  const closeAddMaterialModal = useCallback(() => {
    setAddModalOpen(false);
    resetAddModalForm();
  }, [resetAddModalForm]);

  useEffect(() => {
    if (!addModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (addModalSubmitting) return;
        closeAddMaterialModal();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [addModalOpen, addModalSubmitting, closeAddMaterialModal]);

  const submitAddMaterialModal = useCallback(() => {
    if (!onAddManualRow) return;
    const cid = Number(modCategoryId);
    if (!Number.isFinite(cid) || cid <= 0) return;
    const name = modRawName.trim();
    if (!name) return;
    if (!coerceRawUnitToMaterialSelectValue(modUnit)) return;
    const initialPatch: Partial<SheetRowFieldPatch> = {};
    if (modDesc.trim()) initialPatch.description = modDesc.trim();
    if (modPromo.trim()) initialPatch.promotion = modPromo.trim();
    const discRaw = modDisc.trim();
    if (discRaw) {
      const dn = parseSheetPromotionDiscountInputToNumber(discRaw);
      if (dn != null) initialPatch.promotionDiscount = formatSheetRowPromotionDiscountInputValue(dn);
    }
    if (!modUnquoted) {
      const priceRaw = modPrice.trim();
      if (priceRaw) {
        const pn = parseSheetPriceInputToNumber(priceRaw);
        if (pn != null) initialPatch.price = formatSheetRowPriceInputValue(pn);
      }
    }
    initialPatch.unit = modUnit.trim();
    initialPatch.unquoted = modUnquoted;
    initialPatch.valueAddedTax = modVat;
    setAddModalError('');
    setAddModalSubmitting(true);
    void (async () => {
      try {
        const res = await onAddManualRow({ userCategoryId: cid, rawName: name, initialPatch });
        if (!res.ok) {
          setAddModalError((res.message ?? '').trim() || 'No se pudo registrar el material.');
          return;
        }
        closeAddMaterialModal();
      } catch {
        setAddModalError('No se pudo registrar el material.');
      } finally {
        setAddModalSubmitting(false);
      }
    })();
  }, [
    closeAddMaterialModal,
    modCategoryId,
    modDesc,
    modDisc,
    modPrice,
    modPromo,
    modRawName,
    modUnquoted,
    modUnit,
    modVat,
    onAddManualRow,
  ]);

  useEffect(() => {
    previewRows.forEach((row, i) => {
      if (excludedByRowIndex[i]) return;
      if (rowFieldPatches[i]?.description !== undefined) return;
      const d = getSheetRowDescriptionSeedFromSource(row);
      if (d) patchField(i, { description: d });
    });
    previewRows.forEach((row, i) => {
      if (excludedByRowIndex[i]) return;
      if (rowFieldPatches[i]?.promotion !== undefined) return;
      const p = getSheetRowPromotionSeedFromSource(row);
      if (p) patchField(i, { promotion: p });
    });
    previewRows.forEach((row, i) => {
      if (excludedByRowIndex[i]) return;
      if (rowFieldPatches[i]?.promotionDiscount !== undefined) return;
      const pd = getSheetRowPromotionDiscountSeedFromRow(row);
      if (pd) patchField(i, { promotionDiscount: pd });
    });
  }, [previewRows, patchField, rowFieldPatches, excludedByRowIndex]);

  const mergedRows = useMemo(
    () => previewRows.map((r, i) => applySheetRowFieldPatches(r, rowFieldPatches[i])),
    [previewRows, rowFieldPatches]
  );

  useEffect(() => {
    previewRows.forEach((row, i) => {
      if (excludedByRowIndex[i]) return;
      if (rowFieldPatches[i]?.unquoted !== undefined) return;
      const eff = mergedRows[i];
      if (readSheetRowPriceValue(eff) != null) return;
      patchField(i, { unquoted: true, valueAddedTax: false });
    });
  }, [previewRows, mergedRows, rowFieldPatches, patchField, excludedByRowIndex]);

  const initialRematchByRow = useMemo(
    () => initRematchMapForPreviewRows(previewRows),
    [previewRows]
  );

  const previewCatalogMaterialIdKey = useMemo(() => {
    const ids = collectSheetPreviewCatalogMaterialIds(previewRows);
    return Array.from(ids)
      .sort((a, b) => a - b)
      .join(',');
  }, [previewRows]);

  const previewRowsRef = useRef(previewRows);
  previewRowsRef.current = previewRows;

  useEffect(() => {
    if (commerceMaterialsCategoriesProp !== undefined) {
      setFetchedMaterialsCategories(null);
      return;
    }
    if (commerceId == null) {
      setFetchedMaterialsCategories(null);
      return;
    }
    let cancelled = false;
    void fetchCommerceCatalogMaterialsForPreviewListing(commerceId, previewRowsRef.current)
      .then((cats) => {
        if (cancelled) return;
        setFetchedMaterialsCategories(cats);
      })
      .catch(() => {
        if (!cancelled) setFetchedMaterialsCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [commerceId, commerceMaterialsCategoriesProp, previewCatalogMaterialIdKey]);

  const resolvedMaterialsCategories = useMemo(() => {
    if (commerceMaterialsCategoriesProp !== undefined) return commerceMaterialsCategoriesProp;
    return fetchedMaterialsCategories ?? [];
  }, [commerceMaterialsCategoriesProp, fetchedMaterialsCategories]);

  const offerByCatalogMaterialId = useMemo(
    () => buildCommerceListingOfferByCatalogMaterialId(resolvedMaterialsCategories),
    [resolvedMaterialsCategories]
  );

  const rowMatchesStoredListing = useCallback(
    (eff: unknown) => {
      const mid = readSheetRowCatalogMaterialId(eff);
      if (mid == null || mid <= 0) return false;
      const listing = offerByCatalogMaterialId.get(mid);
      if (listing == null) return false;
      return sheetMergedRowMatchesCommerceListing(eff, listing);
    },
    [offerByCatalogMaterialId]
  );

  const rowHasPublishedCommerceMaterial = useCallback(
    (eff: unknown) => {
      const mid = readSheetRowCatalogMaterialId(eff);
      if (mid == null || mid <= 0) return false;
      return offerByCatalogMaterialId.has(mid);
    },
    [offerByCatalogMaterialId]
  );

  useLayoutEffect(() => {
    const next = new Set<number>();
    previewRows.forEach((row, i) => {
      const mid = readSheetRowCatalogMaterialId(row);
      if (mid == null || mid <= 0) return;
      const listing = offerByCatalogMaterialId.get(mid);
      if (listing == null) return;
      if (sheetMergedRowMatchesCommerceListing(row, listing)) next.add(i);
    });
    unchangedBootstrapRef.current = next;
  }, [previewRows, offerByCatalogMaterialId]);

  useEffect(() => {
    if (!onExcludedChange) return;
    const boot = unchangedBootstrapRef.current;
    mergedRows.forEach((eff, i) => {
      if (!boot.has(i)) return;
      if (!excludedByRowIndex[i]) return;
      const mid = readSheetRowCatalogMaterialId(eff);
      const listing = mid != null ? offerByCatalogMaterialId.get(mid) : undefined;
      if (listing == null || !sheetMergedRowMatchesCommerceListing(eff, listing)) {
        onExcludedChange(i, false);
      }
    });
  }, [mergedRows, excludedByRowIndex, offerByCatalogMaterialId, onExcludedChange]);

  const rowsWithMeta = useMemo(() => {
    return previewRows.map((row, i) => {
      const catalogLinked = rowUsesCatalogDirectConfirm(row);
      const sheetRow =
        row != null && typeof row === 'object' && !Array.isArray(row)
          ? (Number((row as Record<string, unknown>).rowIndex) as number)
          : NaN;
      const displayRow = Number.isFinite(sheetRow) ? sheetRow : i + 1;
      return { row, i, catalogLinked, displayRow };
    });
  }, [previewRows]);

  const normalizedSheetFilter = sheetFilterQuery.trim().toLowerCase();

  const legacyCompletenessBanner = useMemo(
    () => previewRowsMissingCompletenessBlock(previewRows),
    [previewRows]
  );

  const sheetStatusOptionCounts = useMemo(() => {
    let nAll = 0;
    let nIncomplete = 0;
    let nComplete = 0;
    let nUnchanged = 0;
    for (let i = 0; i < previewRows.length; i += 1) {
      const row = previewRows[i];
      const eff = mergedRows[i] ?? row;
      const patch = rowFieldPatches[i];
      const dataComplete = rowMeetsSheetConfirmMinimums(row, i, eff, rematchByRowIndex);
      const matchesStored = rowMatchesStoredListing(eff);
      const hasPublishedMaterial = rowHasPublishedCommerceMaterial(eff);
      if (normalizedSheetFilter) {
        const sheetRowNum =
          row != null && typeof row === 'object' && !Array.isArray(row)
            ? (Number((row as Record<string, unknown>).rowIndex) as number)
            : NaN;
        const displayRowNum = Number.isFinite(sheetRowNum) ? sheetRowNum : i + 1;
        const blob = [
          getSheetRowCategoryDisplay(eff, categories),
          getSheetRowDefaultRawName(eff),
          (rematchByRowIndex[i]?.rawName ?? '').trim(),
          descInputValue(eff, patch),
          descriptionOfferInputValue(eff, patch),
          promotionOfferInputValue(eff, patch),
          String(displayRowNum),
          matchesStored ? 'igual oferta ya cargado' : '',
          hasPublishedMaterial ? 'ya en tus materiales' : '',
        ]
          .join(' ')
          .toLowerCase();
        if (!blob.includes(normalizedSheetFilter)) continue;
      }
      nAll += 1;
      if (!dataComplete) nIncomplete += 1;
      if (dataComplete) nComplete += 1;
      if (matchesStored) nUnchanged += 1;
    }
    return { all: nAll, incomplete: nIncomplete, complete: nComplete, unchanged: nUnchanged };
  }, [
    previewRows,
    mergedRows,
    rematchByRowIndex,
    rowFieldPatches,
    normalizedSheetFilter,
    categories,
    rowMatchesStoredListing,
    rowHasPublishedCommerceMaterial,
  ]);

  const filteredReviewRowIndices = useMemo(() => {
    const out: number[] = [];
    for (let i = 0; i < previewRows.length; i += 1) {
      const row = previewRows[i];
      const eff = mergedRows[i] ?? row;
      const patch = rowFieldPatches[i];
      const dataComplete = rowMeetsSheetConfirmMinimums(row, i, eff, rematchByRowIndex);
      const matchesStored = rowMatchesStoredListing(eff);
      const hasPublishedMaterial = rowHasPublishedCommerceMaterial(eff);
      if (sheetStatusFilter === 'incomplete' && dataComplete) continue;
      if (sheetStatusFilter === 'complete' && !dataComplete) continue;
      if (sheetStatusFilter === 'unchanged' && !matchesStored) continue;
      if (normalizedSheetFilter) {
        const sheetRowNum =
          row != null && typeof row === 'object' && !Array.isArray(row)
            ? (Number((row as Record<string, unknown>).rowIndex) as number)
            : NaN;
        const displayRowNum = Number.isFinite(sheetRowNum) ? sheetRowNum : i + 1;
        const blob = [
          getSheetRowCategoryDisplay(eff, categories),
          getSheetRowDefaultRawName(eff),
          (rematchByRowIndex[i]?.rawName ?? '').trim(),
          descInputValue(eff, patch),
          descriptionOfferInputValue(eff, patch),
          promotionOfferInputValue(eff, patch),
          String(displayRowNum),
          matchesStored ? 'igual oferta ya cargado' : '',
          hasPublishedMaterial ? 'ya en tus materiales' : '',
        ]
          .join(' ')
          .toLowerCase();
        if (!blob.includes(normalizedSheetFilter)) continue;
      }
      out.push(i);
    }
    return out;
  }, [
    previewRows,
    mergedRows,
    rematchByRowIndex,
    rowFieldPatches,
    sheetStatusFilter,
    normalizedSheetFilter,
    categories,
    rowMatchesStoredListing,
    rowHasPublishedCommerceMaterial,
  ]);

  const sheetReviewTotalPages = Math.max(
    1,
    Math.ceil(filteredReviewRowIndices.length / SHEET_REVIEW_UI_PAGE_SIZE)
  );

  useEffect(() => {
    setSheetReviewUIPage((p) => Math.min(Math.max(1, p), sheetReviewTotalPages));
  }, [sheetReviewTotalPages]);

  useEffect(() => {
    setSheetReviewUIPage(1);
  }, [sheetFilterQuery, sheetStatusFilter, previewRows.length]);

  const pagedReviewRowIndices = useMemo(() => {
    const start = (sheetReviewUIPage - 1) * SHEET_REVIEW_UI_PAGE_SIZE;
    return filteredReviewRowIndices.slice(start, start + SHEET_REVIEW_UI_PAGE_SIZE);
  }, [filteredReviewRowIndices, sheetReviewUIPage]);

  const sheetReviewVisibleRangeLabel = useMemo(() => {
    if (filteredReviewRowIndices.length === 0) return '0 resultados con el filtro actual';
    const from = (sheetReviewUIPage - 1) * SHEET_REVIEW_UI_PAGE_SIZE + 1;
    const to = Math.min(sheetReviewUIPage * SHEET_REVIEW_UI_PAGE_SIZE, filteredReviewRowIndices.length);
    return `Filas ${from}–${to} de ${filteredReviewRowIndices.length} con el filtro actual (${previewRows.length} en la hoja)`;
  }, [filteredReviewRowIndices.length, previewRows.length, sheetReviewUIPage]);

  const sheetReviewTableRef = useRef<HTMLDivElement>(null);

  const onSheetReviewPageNav = useCallback(
    (direction: 'prev' | 'next') => {
      setSheetReviewUIPage((p) => {
        if (direction === 'prev') return Math.max(1, p - 1);
        return Math.min(sheetReviewTotalPages, p + 1);
      });
      window.setTimeout(() => {
        const el = sheetReviewTableRef.current;
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.scrollY - SHEET_REVIEW_TABLE_SCROLL_OFFSET_PX;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      }, 0);
    },
    [sheetReviewTotalPages]
  );

  const sheetConfirmPreflight = useMemo(
    () => buildRowsForCommerceSheetConfirm(mergedRows, rematchByRowIndex, excludedByRowIndex),
    [mergedRows, rematchByRowIndex, excludedByRowIndex]
  );

  const pendingBannerText = useMemo(() => {
    if (previewRows.length === 0) return null;
    return sheetConfirmPreflight.error;
  }, [previewRows.length, sheetConfirmPreflight.error]);

  const publishBlocked = previewRows.length > 0 && sheetConfirmPreflight.error != null;
  const canPublishSheet = previewRows.length > 0;

  const displayError = (errorText ?? '').trim() || localError;

  const renderSheetReviewPagination = () => {
    if (sheetReviewTotalPages <= 1) return null;
    return (
      <footer
        className={`${styles.sheetPagination} ${styles.sheetPaginationBottom}`}
        aria-label="Paginación del listado"
      >
        <button type="button" disabled={sheetReviewUIPage <= 1} onClick={() => onSheetReviewPageNav('prev')}>
          Anterior
        </button>
        <span>
          Página {sheetReviewUIPage} de {sheetReviewTotalPages}
        </span>
        <button
          type="button"
          disabled={sheetReviewUIPage >= sheetReviewTotalPages}
          onClick={() => onSheetReviewPageNav('next')}
        >
          Siguiente
        </button>
      </footer>
    );
  };

  const successLines = useMemo(() => {
    if (!savedSummary) return { shown: [] as string[], rest: 0 };
    return sheetConfirmSummaryLinesForDisplay(savedSummary.lines);
  }, [savedSummary]);

  if (savedSummary) {
    return (
      <div className={styles.wrap}>
        <h2 className={`${styles.title} ${styles.titlePublished}`}>Listado publicado</h2>
        <div className={styles.successPanel} role="status">
          <p className={styles.successHeadline}>{savedSummary.headline}</p>
          {savedSummary.failedNote ? <p className={styles.successFailed}>{savedSummary.failedNote}</p> : null}
          {successLines.shown.length > 0 ? (
            <ul className={styles.successList}>
              {successLines.shown.map((line, idx) => (
                <li key={idx} className={styles.successListItem}>
                  {line}
                </li>
              ))}
            </ul>
          ) : null}
          {successLines.rest > 0 ? (
            <p className={styles.successOverflow}>…y {successLines.rest} producto(s) más en el listado.</p>
          ) : null}
          <p className={styles.successFooter}>{profileMaterialsHint}</p>
        </div>
        <div className={styles.actions}>
          <button type="button" className={formStyles.submit} onClick={() => onSuccessContinue()}>
            {successContinueLabel}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.wrap} ${styles.wrapReviewDocked}`}>
      <div className={styles.reviewScrollBlock}>
        <h2 className={styles.title}>Revisión antes de guardar</h2>
        <p className={styles.intro}>
          {previewRows.length > 0
            ? 'Mirá cómo quedó tu listado y ajustá lo que haga falta. Cuando esté bien para vos, publicalo en tu perfil.'
            : 'Agregá materiales de a uno: cada uno se guarda al instante en tu catálogo con la misma validación que al publicar un listado. Si más adelante querés cargar una planilla, volvé atrás y subí un archivo.'}
        </p>
      {legacyCompletenessBanner ? (
        <div className={styles.legacyCompletenessBanner} role="note">
          <Info size={16} aria-hidden />
          <span>
            Este borrador no incluye el bloque de completitud del análisis. Lo ideal es volver a subir o analizar el
            archivo para ver el estado de cada fila. Si seguís acá, revisá nombre, categoría y unidad en las filas que
            sigan incompletas.
          </span>
        </div>
      ) : null}
      {pendingBannerText != null ? (
        <div className={styles.sheetTopBar}>
          <p className={styles.pendingBanner} role="status">
            <Info size={16} />
            {pendingBannerText}
          </p>
        </div>
      ) : null}
      {displayError ? <p className={styles.error}>{displayError}</p> : null}
      {onAddManualRow ? (
        <ModalOverlay
          isOpen={addModalOpen}
          onClose={closeAddMaterialModal}
          preventClose={addModalSubmitting}
          zIndex={32}
        >
          <motion.div
            initial="hidden"
            animate="visible"
            variants={addMaterialPopupVariants}
            className={addMaterialPopupStyles.popup}
          >
            <div className={addMaterialPopupStyles.popup_header}>
              <div>
                <h2 className={addMaterialPopupStyles.popup_header_title}>Agregar material al catálogo</h2>
                <p className={addMaterialPopupStyles.popup_header_subtitle}>
                  Se guarda de inmediato en tu catálogo (misma validación que al publicar un listado). Categoría, nombre
                  y unidad del listado Edify son obligatorios.
                </p>
              </div>
              <button
                type="button"
                className={addMaterialPopupStyles.popup_header_cancelBtn}
                onClick={closeAddMaterialModal}
                disabled={addModalSubmitting}
              >
                Cancelar
              </button>
            </div>
            <form
              className={addMaterialPopupStyles.popup_form}
              onSubmit={(e) => {
                e.preventDefault();
                submitAddMaterialModal();
              }}
            >
              <div className={addMaterialPopupStyles.popup_formBody}>
                {addModalError ? (
                  <p className={styles.error} role="alert">
                    {addModalError}
                  </p>
                ) : null}
                <div className={addMaterialPopupStyles.formSection}>
                  <h3 className={addMaterialPopupStyles.formSectionTitle}>Datos del material</h3>
                  <div className={addMaterialPopupStyles.formGroup}>
                    <label htmlFor="sheet-add-material-category">Categoría</label>
                    <CustomSelect
                      value={modCategoryId}
                      onChange={setModCategoryId}
                      options={categorySelectOptions}
                      placeholder="Seleccioná una categoría"
                      searchable
                      searchPlaceholder="Buscar categoría…"
                    />
                  </div>
                  <div className={addMaterialPopupStyles.formGroup}>
                    <label htmlFor="sheet-add-material-name">Nombre</label>
                    <TextareaAutosize
                      id="sheet-add-material-name"
                      value={modRawName}
                      onChange={(e) => setModRawName(e.target.value)}
                      placeholder="Como en tu listado"
                      minRows={2}
                    />
                  </div>
                  <div className={addMaterialPopupStyles.formGroup}>
                    <label htmlFor="sheet-add-material-desc">Descripción</label>
                    <TextareaAutosize
                      id="sheet-add-material-desc"
                      value={modDesc}
                      onChange={(e) => setModDesc(e.target.value)}
                      minRows={2}
                    />
                  </div>
                  <div className={addMaterialPopupStyles.formGroup}>
                    <label htmlFor="sheet-add-material-promo">Bonificación</label>
                    <input
                      id="sheet-add-material-promo"
                      type="text"
                      value={modPromo}
                      onChange={(e) => setModPromo(e.target.value)}
                    />
                  </div>
                  <div className={addMaterialPopupStyles.formGroup}>
                    <label htmlFor="sheet-add-material-disc">Descuento de promoción (%)</label>
                    <input
                      id="sheet-add-material-disc"
                      type="text"
                      inputMode="decimal"
                      value={modDisc}
                      onChange={(e) => setModDisc(e.target.value)}
                      onBlur={(e) => {
                        const raw = e.target.value.trim();
                        if (!raw) {
                          setModDisc('');
                          return;
                        }
                        const n = parseSheetPromotionDiscountInputToNumber(raw);
                        if (n != null) setModDisc(formatSheetRowPromotionDiscountInputValue(n));
                      }}
                    />
                  </div>
                  <div className={addMaterialPopupStyles.formGroup}>
                    <label htmlFor="sheet-add-material-price">Precio de lista</label>
                    <input
                      id="sheet-add-material-price"
                      type="text"
                      inputMode="decimal"
                      value={modPrice}
                      onChange={(e) => setModPrice(e.target.value)}
                      onBlur={(e) => {
                        const raw = e.target.value.trim();
                        if (!raw) {
                          setModPrice('');
                          return;
                        }
                        const n = parseSheetPriceInputToNumber(raw);
                        if (n != null) setModPrice(formatSheetRowPriceInputValue(n));
                      }}
                      disabled={modUnquoted}
                    />
                  </div>
                  <div className={addMaterialPopupStyles.formGroup}>
                    <label>Unidad</label>
                    <CustomSelect
                      value={modUnit}
                      onChange={setModUnit}
                      options={MATERIAL_UNIT_SELECT_OPTIONS}
                      placeholder="Seleccioná una unidad"
                      searchable
                      searchPlaceholder="Buscar unidad…"
                    />
                  </div>
                  <div className={addMaterialPopupStyles.formCheckboxList}>
                    <label htmlFor="sheet-add-material-unquoted">
                      <input
                        id="sheet-add-material-unquoted"
                        type="checkbox"
                        checked={modUnquoted}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setModUnquoted(on);
                          if (on) {
                            setModPrice('');
                            setModVat(false);
                          } else {
                            setModVat(true);
                          }
                        }}
                      />
                      No informar precio
                    </label>
                    {!modUnquoted ? (
                      <label htmlFor="sheet-add-material-vat">
                        <input
                          id="sheet-add-material-vat"
                          type="checkbox"
                          checked={modVat}
                          onChange={(e) => setModVat(e.target.checked)}
                        />
                        IVA incluido en el precio
                      </label>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className={addMaterialPopupStyles.buttonGroup}>
                <button
                  type="submit"
                  disabled={
                    !modCategoryId ||
                    !modRawName.trim() ||
                    !coerceRawUnitToMaterialSelectValue(modUnit) ||
                    addModalSubmitting
                  }
                  className={addMaterialPopupStyles.buttonGroup_acceptBtn}
                >
                  <CirclePlus size={18} />
                  {addModalSubmitting ? 'Guardando…' : 'Guardar en mi catálogo'}
                </button>
              </div>
            </form>
          </motion.div>
        </ModalOverlay>
      ) : null}
      {previewRows.length > 0 ? (
        <div className={styles.sheetFilterBar}>
          <div className={styles.sheetFilterField}>
            <label className={styles.sheetFilterLabel} htmlFor="sheet-review-filter-q">
              Buscar en el listado
            </label>
            <input
              id="sheet-review-filter-q"
              type="search"
              className={styles.sheetFilterInput}
              value={sheetFilterQuery}
              onChange={(e) => setSheetFilterQuery(e.target.value)}
              placeholder="Nombre, categoría, descripción…"
              autoComplete="off"
            />
          </div>
          <div className={styles.sheetFilterStatus}>
            <label className={styles.sheetFilterLabel} htmlFor="sheet-review-filter-status">
              Estado
            </label>
            <select
              id="sheet-review-filter-status"
              className={`${formStyles.selectMuted} ${styles.sheetFilterStatusSelect}`}
              value={sheetStatusFilter}
              onChange={(e) =>
                setSheetStatusFilter(e.target.value as 'all' | 'incomplete' | 'complete' | 'unchanged')
              }
            >
              <option value="all">Todos ({sheetStatusOptionCounts.all})</option>
              <option value="incomplete">Falta completar ({sheetStatusOptionCounts.incomplete})</option>
              <option value="complete">Listos ({sheetStatusOptionCounts.complete})</option>
              <option value="unchanged">Igual a tu listado (sin cambios) ({sheetStatusOptionCounts.unchanged})</option>
            </select>
          </div>
          {onAddManualRow ? (
            <div className={styles.sheetFilterAddWrap}>
              <button type="button" className={styles.addMaterialButton} onClick={openAddMaterialModal}>
                <Plus size={16} />
                Agregar material al catálogo
              </button>
            </div>
          ) : null}
          <p className={styles.sheetFilterCount} aria-live="polite">
            {sheetReviewVisibleRangeLabel}
          </p>
        </div>
      ) : onAddManualRow ? (
        <div className={styles.sheetFilterBar}>
          <p className={styles.sheetFilterCount} style={{ flex: '1 1 100%', margin: 0 }}>
            No hay filas de planilla en esta pantalla. Usá el botón para dar de alta un material; cada guardado va al
            servidor de inmediato.
          </p>
          <div className={styles.sheetFilterAddWrap}>
            <button type="button" className={styles.addMaterialButton} onClick={openAddMaterialModal}>
              <Plus size={16} />
              Agregar material al catálogo
            </button>
          </div>
        </div>
      ) : null}
      <div ref={sheetReviewTableRef} className={styles.tableWrap}>
        <table className={styles.table}>
          <colgroup>
            <col className={styles.colItem} />
            <col className={styles.colEstado} />
            <col className={styles.colCategoria} />
            <col className={styles.colDetalle} />
            <col className={styles.colPrecio} />
          </colgroup>
          <thead>
            <tr>
              <th className={`${styles.th} ${styles.thItem}`} scope="col">
                Ítem
              </th>
              <th className={styles.th}>Estado</th>
              <th className={styles.th}>Categoría</th>
              <th className={styles.th}>Detalle</th>
              <th className={styles.th} scope="col" aria-describedby="sheet-price-footnote">
                Precio de lista<sup className={styles.thAsterisk}>*</sup>
              </th>
            </tr>
          </thead>
          <tbody>
            {previewRows.length === 0 && onAddManualRow ? (
              <tr>
                <td colSpan={COL_COUNT} className={styles.sheetFilterEmpty}>
                  Todavía no hay filas de archivo para revisar. Usá «Agregar material al catálogo» arriba para cargar
                  productos de a uno.
                </td>
              </tr>
            ) : null}
            {previewRows.length > 0 && filteredReviewRowIndices.length === 0 ? (
              <tr>
                <td colSpan={COL_COUNT} className={styles.sheetFilterEmpty}>
                  Ningún ítem coincide con el filtro. Probá otra búsqueda o cambiá el estado.
                </td>
              </tr>
            ) : null}
            {pagedReviewRowIndices.map((i) => {
              const meta = rowsWithMeta[i];
              if (!meta) return null;
              const { row, catalogLinked, displayRow } = meta;
              const eff = mergedRows[i] ?? row;
              const patch = rowFieldPatches[i];
              const rowExcluded = Boolean(excludedByRowIndex[i]);
              const remPick = rematchByRowIndex[i];
              const resolvedCategoryId = readSheetRowResolvedUserCategoryId(eff);
              const userCatId = remPick?.userCategoryId;
              const fromPickNum =
                userCatId !== '' && userCatId != null ? Number(userCatId) : NaN;
              const effectiveCategoryId =
                Number.isFinite(fromPickNum) && fromPickNum > 0
                  ? fromPickNum
                  : resolvedCategoryId != null && resolvedCategoryId > 0
                    ? resolvedCategoryId
                    : null;
              const categorySelectOutlineInvalid = effectiveCategoryId == null;
              const rawEmpty =
                !catalogLinked && !resolveSheetRematchDisplayedRawName(eff, remPick).length;
              const catalogNameEmpty = catalogLinked && !getSheetRowDefaultRawName(eff).trim();
              const dataComplete = rowMeetsSheetConfirmMinimums(row, i, eff, rematchByRowIndex);
              const matchesStoredListingRow = rowMatchesStoredListing(eff);
              const hasPublishedCommerceMaterial = rowHasPublishedCommerceMaterial(eff);
              const wasSameOfferAtImport = unchangedBootstrapRef.current.has(i);
              const remInit = initialRematchByRow[i] ?? {
                userCategoryId: '' as const,
                rawName: '',
                createCatalogMaterial: false,
                rejectSuggestedCatalogMatch: false,
              };
              const remCur = rematchByRowIndex[i] ?? {
                userCategoryId: '' as const,
                rawName: '',
                createCatalogMaterial: false,
                rejectSuggestedCatalogMatch: false,
              };
              const rowChangedFromImport =
                sheetReviewRowFingerprint(row, rowFieldPatches[i], remCur) !==
                sheetReviewRowFingerprint(row, undefined, remInit);
              const showCommerceMaterialBadge =
                hasPublishedCommerceMaterial &&
                !rowChangedFromImport &&
                (!wasSameOfferAtImport || matchesStoredListingRow);
              const rowIncomplete = !rowExcluded && !dataComplete;
              const inferredCat = getSheetRowCategoryDisplay(eff, categories);
              const reasonHint = readSheetRowReasonCodesHint(row);
              const offerDefaults = readSheetRowOfferDefaults(eff);
              const categorySelectValue =
                effectiveCategoryId != null ? String(effectiveCategoryId) : '';
              return (
                <React.Fragment key={i}>
                  <tr className={rowExcluded ? styles.trExcluded : undefined}>
                    <td
                      className={`${styles.tdItem} ${
                        rowIncomplete ? styles.tdItemIncomplete : styles.tdItemComplete
                      }`}
                    >
                      <div className={styles.itemCell}>
                        <span className={styles.itemOrdinal} aria-hidden>
                          {displayRow}
                        </span>
                        <input
                          type="checkbox"
                          checked={isRowIncluded(excludedByRowIndex, i)}
                          onChange={(e) => {
                            const nextIncluded = e.target.checked;
                            if (
                              nextIncluded &&
                              !rowMeetsSheetConfirmMinimums(row, i, eff, rematchByRowIndex)
                            ) {
                              return;
                            }
                            onExcludedChange?.(i, !nextIncluded);
                          }}
                          aria-label={`Incluir ítem ${displayRow} al publicar. Desmarcá para no publicar.`}
                          title="Marcado: se publica. Sin marcar: no se publica."
                        />
                      </div>
                    </td>
                    <td className={styles.td}>
                      <div className={styles.cellStack}>
                        {rowExcluded ? (
                          <span className={styles.badgeSkip}>No publicar</span>
                        ) : dataComplete ? (
                          <span className={styles.badgeOk}>Listo</span>
                        ) : (
                          <span className={styles.badgeAction}>Completar datos</span>
                        )}
                        {showCommerceMaterialBadge ? (
                          <span
                            className={
                              matchesStoredListingRow
                                ? styles.badgeCommerceListed
                                : styles.badgeCommerceListedWarn
                            }
                          >
                            {matchesStoredListingRow
                              ? 'Ya en tus materiales · misma oferta'
                              : 'Ya en tus materiales · otra oferta'}
                          </span>
                        ) : null}
                        {onRemovePreviewRow ? (
                          <button
                            type="button"
                            className={styles.estadoRemoveRow}
                            onClick={() => onRemovePreviewRow(i)}
                            aria-label="Eliminar este ítem de la lista de revisión"
                          >
                            Eliminar de la lista
                          </button>
                        ) : null}
                        {reasonHint ? (
                          <span className={styles.reasonHint} title={reasonHint}>
                            {reasonHint}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className={styles.tdGrow}>
                      <div className={styles.cellStack}>
                        {inferredCat !== '—' ? (
                          <span className={styles.inferredCat}>Sugerida (IA): {inferredCat}</span>
                        ) : null}
                        <div className={styles.categorySelectWrap}>
                          <CustomSelect
                            value={categorySelectValue}
                            onChange={(v) =>
                              onRematchChange(i, {
                                userCategoryId: v ? Number(v) : '',
                              })
                            }
                            options={categorySelectOptions}
                            placeholder="Elegí categoría…"
                            searchable
                            searchPlaceholder="Buscar categoría…"
                            outlineInvalid={categorySelectOutlineInvalid}
                            selectVisualVariant="compact"
                            title={`Categoría ítem ${displayRow}`}
                          />
                        </div>
                      </div>
                    </td>
                    <td className={styles.tdGrow}>
                      <div className={styles.nameOfferStack}>
                        <label className={styles.offerField}>
                          <span className={styles.offerLabel}>Nombre</span>
                          {catalogLinked ? (
                            <TextareaAutosize
                              className={`${styles.inputDesc} ${catalogNameEmpty ? styles.fieldNeedsValue : ''}`}
                              value={descInputValue(eff, patch)}
                              onChange={(e) => patchField(i, { rawName: e.target.value })}
                              minRows={1}
                              aria-label={`Nombre ítem ${displayRow}`}
                              aria-invalid={catalogNameEmpty}
                            />
                          ) : (
                            <TextareaAutosize
                              className={`${styles.inputDesc} ${rawEmpty ? styles.fieldNeedsValue : ''}`}
                              value={rematchByRowIndex[i]?.rawName ?? ''}
                              onChange={(e) => onRematchChange(i, { rawName: e.target.value })}
                              placeholder="Como en tu listado"
                              minRows={1}
                              aria-invalid={rawEmpty}
                            />
                          )}
                        </label>
                        {catalogLinked && readSheetRowSuggestedCatalogMaterialName(row) ? (
                          <div className={styles.catalogMatchCompare}>
                            <div className={styles.catalogMatchPair}>
                              <span className={styles.catalogMatchLabel}>Nombre sugerido</span>
                              <span className={styles.catalogMatchValue}>
                                {readSheetRowSuggestedCatalogMaterialName(row)}
                              </span>
                            </div>
                            <label className={styles.catalogRejectMatch}>
                              <input
                                type="checkbox"
                                checked={
                                  remPick?.rejectSuggestedCatalogMatch === true &&
                                  remPick?.createCatalogMaterial === true
                                }
                                onChange={(e) => {
                                  const on = e.target.checked;
                                  onRematchChange(i, {
                                    rejectSuggestedCatalogMatch: on,
                                    createCatalogMaterial: on,
                                  });
                                }}
                                aria-label="No es el material sugerido"
                              />
                              <span>No es el material sugerido</span>
                            </label>
                          </div>
                        ) : null}
                        <label className={styles.offerField}>
                          <span className={styles.offerLabel}>Unidad</span>
                          <div className={styles.unitSelectWrap}>
                            <CustomSelect
                              value={unitSelectTableValue(eff, patch)}
                              onChange={(v) => patchField(i, { unit: v })}
                              options={MATERIAL_UNIT_SELECT_OPTIONS}
                              placeholder="Seleccioná una unidad"
                              searchable
                              searchPlaceholder="Buscar unidad…"
                              title={`Unidad ítem ${displayRow}`}
                              selectVisualVariant="compact"
                              outlineInvalidTone="warn"
                              outlineInvalid={
                                rowUnitRequiresKnownSelectValue(row, eff) ||
                                (rowUsesCatalogDirectConfirm(row) &&
                                  remPick?.rejectSuggestedCatalogMatch === true &&
                                  remPick?.createCatalogMaterial === true &&
                                  coerceRawUnitToMaterialSelectValue(unitInputValue(eff, patch)) === '')
                              }
                            />
                          </div>
                        </label>
                        <label className={styles.offerField}>
                          <span className={styles.offerLabel}>Descripción</span>
                          <TextareaAutosize
                            className={styles.inputDesc}
                            value={descriptionOfferInputValue(eff, patch)}
                            onChange={(e) => patchField(i, { description: e.target.value })}
                            minRows={1}
                            aria-label={`Descripción ítem ${displayRow}`}
                          />
                        </label>
                        <label className={styles.offerField}>
                          <span className={styles.offerLabel}>Bonificación</span>
                          <input
                            type="text"
                            className={styles.inputDesc}
                            value={promotionOfferInputValue(eff, patch)}
                            onChange={(e) => patchField(i, { promotion: e.target.value })}
                            aria-label={`Bonificación ítem ${displayRow}`}
                          />
                        </label>
                        <label className={styles.offerField}>
                          <span className={styles.offerLabel}>Descuento de promoción (%)</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            className={styles.inputOfferNarrow}
                            value={promotionDiscountInputValue(eff, patch)}
                            onChange={(e) => patchField(i, { promotionDiscount: e.target.value })}
                            onBlur={(e) => {
                              const raw = e.target.value.trim();
                              if (!raw) {
                                patchField(i, { promotionDiscount: '' });
                                return;
                              }
                              const n = parseSheetPromotionDiscountInputToNumber(raw);
                              if (n != null) {
                                patchField(i, { promotionDiscount: formatSheetRowPromotionDiscountInputValue(n) });
                              }
                            }}
                            aria-label={`Descuento de promoción en porcentaje, ítem ${displayRow}`}
                          />
                        </label>
                      </div>
                    </td>
                    <td className={styles.td}>
                      <div className={styles.cellStack}>
                        {formatSheetRowPriceDisplay(row) !== '—' ? (
                          <span className={styles.inferredCat}>
                            Referencia: {formatSheetRowPriceDisplay(row)}
                          </span>
                        ) : null}
                        <input
                          type="text"
                          inputMode="decimal"
                          className={styles.inputPrice}
                          value={priceInputValue(eff, patch)}
                          onChange={(e) => patchField(i, { price: e.target.value })}
                          onBlur={(e) => {
                            const raw = e.target.value.trim();
                            if (!raw) {
                              patchField(i, { price: '' });
                              return;
                            }
                            const n = parseSheetPriceInputToNumber(raw);
                            if (n != null) {
                              patchField(i, { price: formatSheetRowPriceInputValue(n) });
                            }
                          }}
                          aria-label={`Precio ítem ${displayRow}`}
                          disabled={offerDefaults.unquoted}
                        />
                        <div className={styles.priceColumnChecks}>
                          <label className={styles.priceColumnCheck}>
                            <input
                              type="checkbox"
                              checked={offerDefaults.unquoted}
                              onChange={(e) => {
                                const on = e.target.checked;
                                if (on) {
                                  patchField(i, { unquoted: true, valueAddedTax: false });
                                } else {
                                  patchField(i, { unquoted: false });
                                }
                              }}
                            />
                            <span>No informar precio</span>
                          </label>
                          {!offerDefaults.unquoted ? (
                            <label className={styles.priceColumnCheck}>
                              <input
                                type="checkbox"
                                checked={offerDefaults.valueAddedTax}
                                onChange={(e) => patchField(i, { valueAddedTax: e.target.checked })}
                              />
                              <span>IVA incluido en el precio</span>
                            </label>
                          ) : null}
                        </div>
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <p id="sheet-price-footnote" className={styles.tableFootnote}>
        <span className={styles.footnoteMark}>*</span> Los precios que figuren en la tabla son meramente informativos
        y optativos. Edify no los presenta como oferta real ni como compromiso comercial por parte del comercio.
      </p>
      {renderSheetReviewPagination()}
      </div>
      <div className={styles.reviewActionsDock} role="region" aria-label="Publicar en el perfil">
        <div className={styles.reviewActionsDockInner}>
          <div className={styles.reviewActionsDockText}>
            <p className={styles.reviewActionsDockTitle}>
              {canPublishSheet ? 'Listo para publicar' : 'Publicar planilla'}
            </p>
            <p className={styles.reviewActionsDockHint}>
              {canPublishSheet
                ? 'Al publicar se guarda el listado completo (todas las filas y páginas), con los cambios que hiciste en la tabla.'
                : 'Solo aplica cuando hay filas provenientes de un archivo o borrador. Los materiales que agregás con el formulario ya quedaron guardados al instante.'}
            </p>
          </div>
          <div className={styles.reviewActionsGroup}>
            <button
              type="button"
              className={`${formStyles.submit} ${styles.reviewActionsPrimary}`}
              disabled={submitting || publishBlocked || !canPublishSheet}
              aria-busy={submitting}
              onClick={() => {
                setLocalError('');
                setSubmitting(true);
                void (async () => {
                  try {
                    const result = await onConfirm();
                    if (result.ok) {
                      setSavedSummary(result.summary);
                    } else if (result.ok === false) {
                      const err = result.message.trim();
                      if (err) setLocalError(err);
                    }
                  } finally {
                    setSubmitting(false);
                  }
                })();
              }}
            >
              {submitting ? (
                <span className={styles.confirmWithSpinner}>
                  <span className={styles.spinner} aria-hidden />
                  Guardando…
                </span>
              ) : (
                'Publicar listado en mi perfil'
              )}
            </button>
            <button type="button" className={formStyles.cancel} disabled={submitting} onClick={onBack}>
              Volver
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommerceSheetReviewStep;
